import {castArray} from '@coolcolduk/util';
import type {Question, QuestionStatus} from '@cp/model';
import {useCallback, useRef, useState} from 'react';
import listQuestionsDoc from '../../api/question/listQuestionsDoc';

export interface ListQuestionsVariables {
  moduleId: string;
  topics?: string[];
  difficulty?: number;
  status?: QuestionStatus;
  limit?: number;
  offset?: number;
}

export interface ListQuestionsSingleTopicVariables extends Omit<ListQuestionsVariables, 'topics'> {
  topic?: string;
}

function mapSingleToMultiTopicVariables(
  variables: ListQuestionsSingleTopicVariables | ListQuestionsVariables,
): ListQuestionsVariables {
  const topics =
    'topics' in variables ? variables.topics : castArray((variables as ListQuestionsSingleTopicVariables).topic || []);
  return {
    ...variables,
    topics,
  };
}

export function useListQuestions() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const lastVariablesRef = useRef<ListQuestionsVariables | null>(null);

  const fetchQuestions = useCallback(async (variables: ListQuestionsSingleTopicVariables | ListQuestionsVariables) => {
    const queryVariables = mapSingleToMultiTopicVariables(variables);
    lastVariablesRef.current = queryVariables;
    setLoading(true);
    setError(undefined);
    try {
      const result = await listQuestionsDoc({
        moduleId: queryVariables.moduleId,
        topics: queryVariables.topics,
        difficulty: queryVariables.difficulty,
        status: queryVariables.status,
        limit: queryVariables.limit,
        offset: queryVariables.offset,
      });
      setQuestions(result);
      return result;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(async () => {
    const last = lastVariablesRef.current;
    if (!last?.moduleId) {
      return questions;
    }
    return fetchQuestions(last);
  }, [fetchQuestions, questions]);

  return {
    questions,
    loading,
    error,
    fetchQuestions,
    refetch,
  };
}
